import { Navigate, useLocation } from 'react-router';
import { useAuth } from './AuthContext';
import { Permission, useHasPermission } from '@/utils/roles';
import { NoAccessPage } from '@/components';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
}

export const ProtectedRoute = ({
  children,
  requiredPermissions = [],
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    hasPermission,
    isLoading: userLoading,
    currentUser,
  } = useHasPermission(requiredPermissions ?? []);
  const location = useLocation();

  // Show loading while checking authentication or user profile
  if (authLoading || userLoading) {
    return (
      <div
        className="flex h-screen items-center justify-center text-lg"
        aria-label="Loading"
        tabIndex={0}
      >
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if required (only after loading is complete)
  if (requiredPermissions.length > 0 && currentUser && !hasPermission) {
    return <NoAccessPage />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
